import React, { useState, useEffect, useCallback, useMemo, memo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { OptimizedInput } from '@/components/ui/optimized-input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  UserPlus, Trash2, Users, Mail, Pencil, Key, BookOpen, Shield, 
  Building2, ArrowLeft, Plus, ChevronRight, Compass, Edit, Lock, Unlock, AlertTriangle,
  Clock, FileText, RefreshCw as RefreshIcon, Upload, FileSpreadsheet, Megaphone, ArrowUpCircle, ShieldAlert, RotateCcw, Download
} from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import firstName from '@/utils/firstName';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const didAutoOpenRef = useRef(false);
  // View state
  const [currentView, setCurrentView] = useState('institutions'); // 'institutions' or 'institution-detail'
  const [selectedInstitution, setSelectedInstitution] = useState(null);
  
  // Data state
  const [institutions, setInstitutions] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [controlEscolarUsers, setControlEscolarUsers] = useState([]);
  const [orientadores, setOrientadores] = useState([]);
  const [jefeDocentes, setJefeDocentes] = useState([]);
  const [jefeTutores, setJefeTutores] = useState([]);
  const [prefectos, setPrefectos] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Institution dialogs
  const [instDialogOpen, setInstDialogOpen] = useState(false);
  const [editInstDialogOpen, setEditInstDialogOpen] = useState(false);
  const [newInstitution, setNewInstitution] = useState({ name: '' });
  const [editingInstitution, setEditingInstitution] = useState(null);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [onlineTeachers, setOnlineTeachers] = useState(new Set());
  
  // Teacher dialogs
  const [teacherDialogOpen, setTeacherDialogOpen] = useState(false);
  const [newTeacher, setNewTeacher] = useState({ name: '', email: '', password: '', institution_id: '' });
  const [editTeacherDialogOpen, setEditTeacherDialogOpen] = useState(false);
  const [editingTeacher, setEditingTeacher] = useState(null);
  const [editTeacherForm, setEditTeacherForm] = useState({ name: '', email: '' });
  
  // Control Escolar dialogs
  const [ceDialogOpen, setCeDialogOpen] = useState(false);
  const [newCE, setNewCE] = useState({ name: '', email: '', password: '', institution_id: '' });
  
  // Orientador dialogs
  const [orientadorDialogOpen, setOrientadorDialogOpen] = useState(false);
  const [newOrientador, setNewOrientador] = useState({ name: '', email: '', password: '', institution_id: '' });

  // Jefe Docentes dialogs
  const [jdDialogOpen, setJdDialogOpen] = useState(false);
  const [newJD, setNewJD] = useState({ name: '', email: '', password: '', institution_id: '' });

  // Jefe Tutores dialogs
  const [jtDialogOpen, setJtDialogOpen] = useState(false);
  const [newJT, setNewJT] = useState({ name: '', email: '', password: '', institution_id: '' });
  
  // Reset password state
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetUser, setResetUser] = useState(null); // {id, name, role: 'teacher'|'control_escolar'}
  const [newPassword, setNewPassword] = useState('');
  
  // Change institution dialog
  const [changeInstDialogOpen, setChangeInstDialogOpen] = useState(false);
  const [changingItem, setChangingItem] = useState(null); // {type: 'user'|'group', id, currentInstitution}
  const [newInstitutionId, setNewInstitutionId] = useState('');
  
  // View classes state
  const [classesDialogOpen, setClassesDialogOpen] = useState(false);
  const [viewingTeacher, setViewingTeacher] = useState(null);
  const [teacherClasses, setTeacherClasses] = useState([]);
  
  // Delete teacher confirmation state
  const [deleteTeacherDialogOpen, setDeleteTeacherDialogOpen] = useState(false);
  const [teacherToDelete, setTeacherToDelete] = useState(null);
  const [teacherClassesToDelete, setTeacherClassesToDelete] = useState([]);
  const [deletingTeacher, setDeletingTeacher] = useState(false);

  // Delete group confirmation state
  const [deleteGroupDialogOpen, setDeleteGroupDialogOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [groupClassesToDelete, setGroupClassesToDelete] = useState([]);
  const [deletingGroup, setDeletingGroup] = useState(false);
  const [groupHasLinkedClasses, setGroupHasLinkedClasses] = useState(false);

  // Delete Control Escolar confirmation state
  const [deleteCEDialogOpen, setDeleteCEDialogOpen] = useState(false);
  const [ceToDelete, setCeToDelete] = useState(null);

  // Delete Orientador confirmation state
  const [deleteOrientadorDialogOpen, setDeleteOrientadorDialogOpen] = useState(false);
  const [orientadorToDelete, setOrientadorToDelete] = useState(null);
  // Prefecto dialogs
  const [prefectoDialogOpen, setPrefectoDialogOpen] = useState(false);
  const [newPrefecto, setNewPrefecto] = useState({ name: '', email: '', password: '', institution_id: '' });
  const [deletePrefectoDialogOpen, setDeletePrefectoDialogOpen] = useState(false);
  const [prefectoToDelete, setPrefectoToDelete] = useState(null);
  // Subdirector dialogs
  const [subdirectores, setSubdirectores] = useState([]);
  const [subdirectorDialogOpen, setSubdirectorDialogOpen] = useState(false);
  const [newSubdirector, setNewSubdirector] = useState({ name: '', email: '', password: '', institution_id: '' });
  const [deleteSubdirectorDialogOpen, setDeleteSubdirectorDialogOpen] = useState(false);
  const [subdirectorToDelete, setSubdirectorToDelete] = useState(null);

  // TI Access stats
  const [tiAccessStats, setTiAccessStats] = useState(null);
  const [loadingTiAccess, setLoadingTiAccess] = useState(false);

  // Classes management state
  const [institutionClasses, setInstitutionClasses] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [editClassDialogOpen, setEditClassDialogOpen] = useState(false);
  const [classToEdit, setClassToEdit] = useState(null);
  const [editClassForm, setEditClassForm] = useState({ subject: '', description: '', group_id: '' });
  const [deleteClassDialogOpen, setDeleteClassDialogOpen] = useState(false);
  const [classToDelete, setClassToDelete] = useState(null);
  const [deletingClass, setDeletingClass] = useState(false);
  const [deleteAllClassesDialogOpen, setDeleteAllClassesDialogOpen] = useState(false);
  const [deletingAllClasses, setDeletingAllClasses] = useState(false);

  // Audit log state
  const [auditLog, setAuditLog] = useState([]);
  const [loadingAuditLog, setLoadingAuditLog] = useState(false);
  const [showAuditLog, setShowAuditLog] = useState(false);

  // Group management state
  const [uploadGroupDialogOpen, setUploadGroupDialogOpen] = useState(false);
  const [uploadGroupName, setUploadGroupName] = useState('');
  const [uploadGroupFile, setUploadGroupFile] = useState(null);
  // Group selection + Excel download
  const [selectedGroupIds, setSelectedGroupIds] = useState(() => new Set());
  const [downloadingGroups, setDownloadingGroups] = useState(false);
  // Per-institution period mode (only "3_classic" is selectable today; the rest
  // are reserved for future separator/partial logic modules).
  const PERIOD_MODE_OPTIONS = [
    { value: '3_classic', label: '3 Periodos (clásico)', enabled: true },
    { value: '2_periodos', label: '2 Periodos', enabled: false },
    { value: '4_periodos', label: '4 Periodos', enabled: false },
    { value: '5_periodos', label: '5 Periodos', enabled: false },
    { value: '6_periodos', label: '6 Periodos', enabled: false },
  ];
  // Matrícula update (merge import)
  const [matriculaDialogOpen, setMatriculaDialogOpen] = useState(false);
  const [matriculaGroup, setMatriculaGroup] = useState(null);
  const [matriculaFile, setMatriculaFile] = useState(null);
  const [updatingMatriculas, setUpdatingMatriculas] = useState(false);
  const [matriculaResult, setMatriculaResult] = useState(null);
  const matriculaFileRef = useRef(null);
  const [uploadingGroup, setUploadingGroup] = useState(false);
  const uploadFileRef = React.useRef(null);
  const [viewGroupDialog, setViewGroupDialog] = useState(false);
  const [viewingGroup, setViewingGroup] = useState(null);
  const [editGroupDialog, setEditGroupDialog] = useState(false);
  const [editGroupName, setEditGroupName] = useState('');
  const [editingGroupObj, setEditingGroupObj] = useState(null);

  // Notify professors state
  const [notifyProfsDialogOpen, setNotifyProfsDialogOpen] = useState(false);
  const [notifyProfsInstId, setNotifyProfsInstId] = useState('');
  const [notifyProfsFecha, setNotifyProfsFecha] = useState('');
  const [sendingProfNotif, setSendingProfNotif] = useState(false);

  // Scale groups state
  const [scaleGroupsDialogOpen, setScaleGroupsDialogOpen] = useState(false);
  const [scaleGroupsInstId, setScaleGroupsInstId] = useState('');
  const [scalingGroups, setScalingGroups] = useState(false);
  const [adminsList, setAdminsList] = useState([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(!!user?.is_super_admin);
  const [adminDialogOpen, setAdminDialogOpen] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ name: '', email: '', password: '' });
  const [creatingAdmin, setCreatingAdmin] = useState(false);
  const [activityDialog, setActivityDialog] = useState({ open: false, loading: false, data: null });

  const fetchAdminActivity = async (adminId) => {
    setActivityDialog({ open: true, loading: true, data: null });
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/admin/admins/${adminId}/activity`, { headers: { Authorization: `Bearer ${token}` } });
      setActivityDialog({ open: true, loading: false, data: r.data });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cargar actividad');
      setActivityDialog({ open: false, loading: false, data: null });
    }
  };

  const fetchAdmins = async () => {
    const token = localStorage.getItem('token');
    try {
      const r = await axios.get(`${API}/admin/admins`, { headers: { Authorization: `Bearer ${token}` } });
      setAdminsList(r.data || []);
      const me = (r.data || []).find(a => a.is_me);
      if (me) setIsSuperAdmin(!!me.is_super_admin);
    } catch {}
  };

  useEffect(() => {
    fetchInstitutions();
    fetchAdmins();
    // Load maintenance status
    const token = localStorage.getItem('token');
    axios.get(`${API}/admin/maintenance`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setMaintenanceMode(r.data.active))
      .catch(() => {});
  }, []);

  // Poll online teachers every 30s
  useEffect(() => {
    const fetchOnline = () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      axios.get(`${API}/admin/teachers/online`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setOnlineTeachers(new Set((r.data.online || []).map(t => t.id))))
        .catch(() => {});
    };
    fetchOnline();
    const onFocus = () => { if (document.visibilityState === 'visible') fetchOnline(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  // Auto-open an institution's detail when returning from an access report (?inst=ID)
  useEffect(() => {
    if (didAutoOpenRef.current) return;
    const instId = searchParams.get('inst');
    if (!instId || institutions.length === 0) return;
    const match = institutions.find(i => i.id === instId);
    if (match) {
      didAutoOpenRef.current = true;
      openInstitutionDetail(match);
      setSearchParams({}, { replace: true });
    }
  }, [institutions, searchParams]);

  const fetchInstitutions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/institutions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInstitutions(response.data);
    } catch (error) {
      console.error('Error fetching institutions:', error);
      toast.error('Error al cargar instituciones');
    } finally {
      setLoading(false);
    }
  };

  const toggleMaintenance = async () => {
    const newState = !maintenanceMode;
    const msg = newState ? '¿Activar modo mantenimiento? Los usuarios no podrán acceder.' : '¿Desactivar modo mantenimiento? Los usuarios podrán acceder nuevamente.';
    if (!window.confirm(msg)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/maintenance`, { active: newState }, { headers: { Authorization: `Bearer ${token}` } });
      setMaintenanceMode(newState);
      toast.success(newState ? 'Modo mantenimiento activado' : 'Modo mantenimiento desactivado');
    } catch (e) { toast.error('Error al cambiar modo mantenimiento'); }
  };


  const fetchInstitutionDetails = async (institutionId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/institutions/${institutionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedInstitution(response.data.institution);
      setTeachers(response.data.teachers);
      setControlEscolarUsers(response.data.control_escolar);
      setOrientadores(response.data.orientadores || []);
      setJefeDocentes(response.data.jefe_docentes || []);
      setJefeTutores(response.data.jefe_tutores || []);
      setPrefectos(response.data.prefectos || []);
      setSubdirectores(response.data.subdirectores || []);
      setGroups(response.data.groups);
      
      // Also fetch classes for this institution
      fetchInstitutionClasses(institutionId);
    } catch (error) {
      console.error('Error fetching institution details:', error);
      toast.error('Error al cargar detalles de institución');
    } finally {
      setLoading(false);
    }
  };

  const fetchInstitutionClasses = async (institutionId) => {
    setLoadingClasses(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/institutions/${institutionId}/classes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInstitutionClasses(response.data);
    } catch (error) {
      console.error('Error fetching classes:', error);
    } finally {
      setLoadingClasses(false);
    }
  };

  const fetchUnassignedData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      // Fetch all data without institution
      const [teachersRes, ceRes, orientadoresRes, groupsRes] = await Promise.all([
        axios.get(`${API}/admin/teachers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/control-escolar`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/orientadores`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/groups`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      // Filter unassigned
      setTeachers(teachersRes.data.filter(t => !t.institution_id));
      setControlEscolarUsers(ceRes.data.filter(c => !c.institution_id));
      setOrientadores(orientadoresRes.data.filter(o => !o.institution_id));
      setGroups(groupsRes.data.filter(g => !g.institution_id));
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const openInstitutionDetail = (institution) => {
    if (institution === null) {
      // "Sin institución" view
      setSelectedInstitution({ id: null, name: 'Sin Institución Asignada' });
      fetchUnassignedData();
    } else {
      fetchInstitutionDetails(institution.id);
    }
    setCurrentView('institution-detail');
  };

  const goBackToInstitutions = () => {
    setCurrentView('institutions');
    setSelectedInstitution(null);
    fetchInstitutions();
  };

  // Institution CRUD
  const handleCreateInstitution = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/institutions`, newInstitution, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Institución creada');
      setInstDialogOpen(false);
      setNewInstitution({ name: '' });
      fetchInstitutions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear institución');
    }
  };

  const handleEditInstitution = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/institutions/${editingInstitution.id}`, 
        { name: editingInstitution.name },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Institución actualizada');
      setEditInstDialogOpen(false);
      fetchInstitutions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    }
  };

  const handleDeleteInstitution = async (institutionId) => {
    if (!window.confirm('¿Eliminar esta institución? Debe estar vacía.')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/institutions/${institutionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Institución eliminada');
      fetchInstitutions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    if (!newAdmin.name.trim() || !newAdmin.email.trim() || !newAdmin.password) {
      toast.error('Todos los campos son requeridos');
      return;
    }
    setCreatingAdmin(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/admins`, newAdmin, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Administrador creado correctamente');
      setNewAdmin({ name: '', email: '', password: '' });
      setAdminDialogOpen(false);
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear administrador');
    } finally {
      setCreatingAdmin(false);
    }
  };

  const handleDeleteAdmin = async (adminId, adminName) => {
    if (!window.confirm(`¿Eliminar al administrador "${adminName}"? Esta acción es irreversible.`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/admins/${adminId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Administrador eliminado');
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleResetAdminPassword = async (adminId, adminName) => {
    const newPass = window.prompt(`Nueva contraseña para ${adminName} (mín. 6 caracteres):`);
    if (!newPass || newPass.length < 6) {
      if (newPass !== null) toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/admins/${adminId}/reset-password`,
        { new_password: newPass },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Contraseña reseteada. El admin deberá cambiarla al ingresar.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al resetear contraseña');
    }
  };

  // Toggle institution active status
  const handleToggleInstitutionStatus = async (institution) => {
    const isCurrentlyActive = institution.is_active !== false;
    const action = isCurrentlyActive ? 'bloquear' : 'reactivar';
    const message = isCurrentlyActive 
      ? `¿Desea BLOQUEAR temporalmente el acceso a ${institution.name}?\n\n• Los usuarios no podrán iniciar sesión\n• Los datos NO serán eliminados\n• Puede reactivar el acceso en cualquier momento`
      : `¿Desea REACTIVAR el acceso a ${institution.name}?\n\nTodos los usuarios podrán volver a iniciar sesión normalmente.`;
    
    if (!window.confirm(message)) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(
        `${API}/admin/institutions/${institution.id}/toggle-status`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      fetchInstitutions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar estado');
    }
  };

  // Teacher CRUD
  const handleCreateTeacher = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const data = {
        ...newTeacher,
        institution_id: newTeacher.institution_id || (selectedInstitution?.id || null)
      };
      await axios.post(`${API}/admin/teachers`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Profesor creado');
      setTeacherDialogOpen(false);
      setNewTeacher({ name: '', email: '', password: '', institution_id: '' });
      if (selectedInstitution?.id) {
        fetchInstitutionDetails(selectedInstitution.id);
      } else if (selectedInstitution) {
        fetchUnassignedData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear profesor');
    }
  };

  const handleEditTeacher = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/teachers/${editingTeacher.id}`, editTeacherForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Profesor actualizado');
      setEditTeacherDialogOpen(false);
      if (selectedInstitution?.id) {
        fetchInstitutionDetails(selectedInstitution.id);
      } else {
        fetchUnassignedData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    }
  };

  const handleDeleteTeacher = async (teacherId, confirmDeleteClasses = false) => {
    try {
      const token = localStorage.getItem('token');
      const url = confirmDeleteClasses 
        ? `${API}/admin/teachers/${teacherId}?confirm_delete_classes=true`
        : `${API}/admin/teachers/${teacherId}`;
      
      const response = await axios.delete(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Check if confirmation is required
      if (response.data.requires_confirmation) {
        // Store info and show confirmation dialog
        const teacher = teachers.find(t => t.id === teacherId);
        setTeacherToDelete({ id: teacherId, name: teacher?.name || 'Profesor' });
        setTeacherClassesToDelete(response.data.classes || []);
        setDeleteTeacherDialogOpen(true);
        return;
      }
      
      // Success - close dialog and refresh
      setDeleteTeacherDialogOpen(false);
      setTeacherToDelete(null);
      setTeacherClassesToDelete([]);
      toast.success(response.data.message || 'Profesor eliminado');
      
      if (selectedInstitution?.id) {
        fetchInstitutionDetails(selectedInstitution.id);
      } else {
        fetchUnassignedData();
      }
    } catch (error) {
      toast.error('Error al eliminar profesor');
    }
  };

  const handleConfirmDeleteTeacherWithClasses = async () => {
    if (!teacherToDelete) return;
    setDeletingTeacher(true);
    await handleDeleteTeacher(teacherToDelete.id, true);
    setDeletingTeacher(false);
  };

  const handleCancelDeleteTeacher = () => {
    setDeleteTeacherDialogOpen(false);
    setTeacherToDelete(null);
    setTeacherClassesToDelete([]);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      // Determine endpoint based on user role
      let endpoint;
      if (resetUser.role === 'control_escolar') {
        endpoint = `${API}/admin/control-escolar/${resetUser.id}/reset-password`;
      } else if (resetUser.role === 'orientador') {
        endpoint = `${API}/admin/orientadores/${resetUser.id}/reset-password`;
      } else if (resetUser.role === 'jefe_docentes') {
        endpoint = `${API}/admin/jefe-docentes/${resetUser.id}/reset-password`;
      } else if (resetUser.role === 'jefe_tutores') {
        endpoint = `${API}/admin/jefe-tutores/${resetUser.id}/reset-password`;
      } else if (resetUser.role === 'prefectura') {
        endpoint = `${API}/admin/prefectos/${resetUser.id}/reset-password`;
      } else if (resetUser.role === 'subdirector') {
        endpoint = `${API}/admin/subdirectores/${resetUser.id}/reset-password`;
      } else {
        endpoint = `${API}/admin/teachers/${resetUser.id}/reset-password`;
      }
      
      await axios.post(endpoint, 
        { new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Contraseña reseteada');
      setResetDialogOpen(false);
      setNewPassword('');
      setResetUser(null);
    } catch (error) {
      toast.error('Error al resetear contraseña');
    }
  };

  // Control Escolar CRUD
  const handleCreateCE = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const data = {
        ...newCE,
        institution_id: newCE.institution_id || (selectedInstitution?.id || null)
      };
      await axios.post(`${API}/admin/control-escolar`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Usuario Control Escolar creado');
      setCeDialogOpen(false);
      setNewCE({ name: '', email: '', password: '', institution_id: '' });
      if (selectedInstitution?.id) {
        fetchInstitutionDetails(selectedInstitution.id);
      } else {
        fetchUnassignedData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear usuario');
    }
  };

  const openDeleteCEDialog = (ce) => {
    setCeToDelete(ce);
    setDeleteCEDialogOpen(true);
  };

  const handleDeleteCE = async () => {
    if (!ceToDelete) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/control-escolar/${ceToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Usuario eliminado');
      setDeleteCEDialogOpen(false);
      setCeToDelete(null);
      if (selectedInstitution?.id) {
        fetchInstitutionDetails(selectedInstitution.id);
      } else {
        fetchUnassignedData();
      }
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // Orientador CRUD
  const handleCreateOrientador = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const data = {
        ...newOrientador,
        institution_id: newOrientador.institution_id || (selectedInstitution?.id || null)
      };
      await axios.post(`${API}/admin/orientadores`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Orientador creado');
      setOrientadorDialogOpen(false);
      setNewOrientador({ name: '', email: '', password: '', institution_id: '' });
      if (selectedInstitution?.id) {
        fetchInstitutionDetails(selectedInstitution.id);
      } else {
        fetchUnassignedData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear orientador');
    }
  };

  const openDeleteOrientadorDialog = (orientador) => {
    setOrientadorToDelete(orientador);
    setDeleteOrientadorDialogOpen(true);
  };

  const handleDeleteOrientador = async () => {
    if (!orientadorToDelete) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/orientadores/${orientadorToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Orientador eliminado');
      setDeleteOrientadorDialogOpen(false);
      setOrientadorToDelete(null);
      if (selectedInstitution?.id) {
        fetchInstitutionDetails(selectedInstitution.id);
      } else {
        fetchUnassignedData();
      }
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // Prefecto CRUD
  const handleCreatePrefecto = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const data = {
        ...newPrefecto,
        institution_id: newPrefecto.institution_id || (selectedInstitution?.id || null)
      };
      await axios.post(`${API}/admin/prefectos`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Prefecto creado');
      setPrefectoDialogOpen(false);
      setNewPrefecto({ name: '', email: '', password: '', institution_id: '' });
      if (selectedInstitution?.id) {
        fetchInstitutionDetails(selectedInstitution.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear prefecto');
    }
  };

  const openDeletePrefectoDialog = (prefecto) => {
    setPrefectoToDelete(prefecto);
    setDeletePrefectoDialogOpen(true);
  };

  const handleDeletePrefecto = async () => {
    if (!prefectoToDelete) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/prefectos/${prefectoToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Prefecto eliminado');
      setDeletePrefectoDialogOpen(false);
      setPrefectoToDelete(null);
      if (selectedInstitution?.id) {
        fetchInstitutionDetails(selectedInstitution.id);
      }
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // Subdirector CRUD
  const handleCreateSubdirector = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const data = {
        ...newSubdirector,
        institution_id: newSubdirector.institution_id || (selectedInstitution?.id || null)
      };
      await axios.post(`${API}/admin/subdirectores`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Subdirector creado');
      setSubdirectorDialogOpen(false);
      setNewSubdirector({ name: '', email: '', password: '', institution_id: '' });
      if (selectedInstitution?.id) {
        fetchInstitutionDetails(selectedInstitution.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear subdirector');
    }
  };

  const openDeleteSubdirectorDialog = (subdirector) => {
    setSubdirectorToDelete(subdirector);
    setDeleteSubdirectorDialogOpen(true);
  };

  const handleDeleteSubdirector = async () => {
    if (!subdirectorToDelete) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/subdirectores/${subdirectorToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Subdirector eliminado');
      setDeleteSubdirectorDialogOpen(false);
      setSubdirectorToDelete(null);
      if (selectedInstitution?.id) {
        fetchInstitutionDetails(selectedInstitution.id);
      }
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // Jefe Docentes CRUD
  const handleCreateJefeDocentes = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const data = { ...newJD, institution_id: newJD.institution_id || (selectedInstitution?.id || '') };
      await axios.post(`${API}/admin/jefe-docentes`, data, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Jefe de Docentes creado');
      setJdDialogOpen(false);
      setNewJD({ name: '', email: '', password: '', institution_id: '' });
      if (selectedInstitution?.id) fetchInstitutionDetails(selectedInstitution.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear');
    }
  };

  const handleDeleteJefeDocentes = async (jdId) => {
    if (!window.confirm('¿Eliminar este Jefe de Docentes?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/jefe-docentes/${jdId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Jefe de Docentes eliminado');
      if (selectedInstitution?.id) fetchInstitutionDetails(selectedInstitution.id);
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleCreateJefeTutores = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const data = { ...newJT, institution_id: newJT.institution_id || (selectedInstitution?.id || '') };
      await axios.post(`${API}/admin/jefe-tutores`, data, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Jefe de Tutores creado');
      setJtDialogOpen(false);
      setNewJT({ name: '', email: '', password: '', institution_id: '' });
      if (selectedInstitution?.id) fetchInstitutionDetails(selectedInstitution.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear');
    }
  };

  const handleDeleteJefeTutores = async (jtId) => {
    if (!window.confirm('¿Eliminar este Jefe de Tutores?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/jefe-tutores/${jtId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Jefe de Tutores eliminado');
      if (selectedInstitution?.id) fetchInstitutionDetails(selectedInstitution.id);
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const fetchTiAccessStats = async () => {
    if (!selectedInstitution?.id) return;
    setLoadingTiAccess(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/ti-access-stats/${selectedInstitution.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setTiAccessStats(res.data);
    } catch (e) { toast.error('Error al cargar estadísticas'); }
    finally { setLoadingTiAccess(false); }
  };

  // Delete Group
  const handleDeleteGroup = async (groupId, groupName, confirmCascade = false) => {
    try {
      const token = localStorage.getItem('token');
      const url = confirmCascade 
        ? `${API}/admin/groups/${groupId}?confirm_cascade=true`
        : `${API}/admin/groups/${groupId}`;
      
      const response = await axios.delete(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Check if confirmation is required
      if (response.data.requires_confirmation) {
        setGroupToDelete({ id: groupId, name: groupName });
        setGroupClassesToDelete(response.data.classes || []);
        setGroupHasLinkedClasses(response.data.has_linked_classes || false);
        setDeleteGroupDialogOpen(true);
        return;
      }
      
      // Success - close dialog and refresh
      setDeleteGroupDialogOpen(false);
      setGroupToDelete(null);
      setGroupClassesToDelete([]);
      setGroupHasLinkedClasses(false);
      toast.success(response.data.message || 'Grupo eliminado');
      
      if (selectedInstitution?.id) {
        fetchInstitutionDetails(selectedInstitution.id);
      } else {
        fetchUnassignedData();
      }
      fetchInstitutions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar grupo');
    }
  };

  const handleConfirmDeleteGroupWithClasses = async () => {
    if (!groupToDelete) return;
    setDeletingGroup(true);
    await handleDeleteGroup(groupToDelete.id, groupToDelete.name, true);
    setDeletingGroup(false);
  };

  const handleCancelDeleteGroup = () => {
    setDeleteGroupDialogOpen(false);
    setGroupToDelete(null);
    setGroupClassesToDelete([]);
    setGroupHasLinkedClasses(false);
  };

  // Class Management Functions
  const openEditClassDialog = (cls) => {
    setClassToEdit(cls);
    setEditClassForm({
      subject: cls.subject || '',
      description: cls.description || '',
      group_id: cls.group_id || ''
    });
    setEditClassDialogOpen(true);
  };

  const handleEditClass = async () => {
    if (!classToEdit) return;
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/classes/${classToEdit.id}`, editClassForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Clase actualizada correctamente');
      setEditClassDialogOpen(false);
      setClassToEdit(null);
      if (selectedInstitution?.id) {
        fetchInstitutionClasses(selectedInstitution.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar clase');
    }
  };

  const openDeleteClassDialog = (cls) => {
    setClassToDelete(cls);
    setDeleteClassDialogOpen(true);
  };

  const handleDeleteClass = async () => {
    if (!classToDelete) return;
    setDeletingClass(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/classes/${classToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Clase eliminada correctamente');
      setDeleteClassDialogOpen(false);
      setClassToDelete(null);
      if (selectedInstitution?.id) {
        fetchInstitutionClasses(selectedInstitution.id);
        fetchInstitutionDetails(selectedInstitution.id);
      }
      fetchInstitutions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar clase');
    } finally {
      setDeletingClass(false);
    }
  };

  const handleDeleteAllClasses = async () => {
    if (!selectedInstitution?.id) return;
    setDeletingAllClasses(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API}/admin/institutions/${selectedInstitution.id}/classes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message || 'Todas las clases eliminadas');
      setDeleteAllClassesDialogOpen(false);
      fetchInstitutionClasses(selectedInstitution.id);
      fetchInstitutionDetails(selectedInstitution.id);
      fetchInstitutions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar clases');
    } finally {
      setDeletingAllClasses(false);
    }
  };

  const fetchAuditLog = async () => {
    setLoadingAuditLog(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/audit-log`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAuditLog(response.data);
    } catch (error) {
      console.error('Error fetching audit log:', error);
    } finally {
      setLoadingAuditLog(false);
    }
  };

  const sendNotifyProfessors = async () => {
    if (!notifyProfsFecha) {
      toast.error('La fecha de borrado es requerida');
      return;
    }
    setSendingProfNotif(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/institutions/${notifyProfsInstId}/notify-professors`, {
        fecha_borrado: notifyProfsFecha
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Aviso enviado a los profesores');
      setNotifyProfsDialogOpen(false);
      setNotifyProfsFecha('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar aviso');
    } finally {
      setSendingProfNotif(false);
    }
  };

  const handleScaleGroups = async () => {
    setScalingGroups(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/institutions/${scaleGroupsInstId}/scale-groups`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data;
      toast.success(`${data.message}. Escalados: ${data.scaled}, Eliminados: ${data.deleted}`);
      setScaleGroupsDialogOpen(false);
      fetchInstitutionDetails(scaleGroupsInstId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al escalar grupos');
    } finally {
      setScalingGroups(false);
    }
  };

  // Group management functions
  const downloadImportTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/groups/import-template`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Plantilla_Grupo.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Error al descargar la plantilla');
    }
  };

  const handleUploadGroup = async (e) => {
    e.preventDefault();
    if (!uploadGroupFile) {
      toast.error('Selecciona un archivo Excel');
      return;
    }
    setUploadingGroup(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', uploadGroupFile);
      const url = `${API}/admin/groups/upload?institution_id=${selectedInstitution.id}`;
      const res = await axios.post(url, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Grupo importado correctamente');
      const warns = res.data?.warnings || [];
      if (warns.length) {
        toast.warning(`Atención: ${warns.length} matrícula(s) ya existían en el sistema con otro alumno`, {
          description: 'Al crear las clases se usará el nombre del archivo importado. Verifica: ' +
            warns.map(w => `${w.matricula}: "${w.nombre_existente}" → "${w.nombre_importado}"`).join('  •  '),
          duration: 15000
        });
      }
      setUploadGroupDialogOpen(false);
      setUploadGroupName('');
      setUploadGroupFile(null);
      if (uploadFileRef.current) uploadFileRef.current.value = '';
      fetchInstitutionDetails(selectedInstitution.id);
      fetchInstitutions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al importar grupo');
    } finally {
      setUploadingGroup(false);
    }
  };

  // Live validation: name derived from the chosen file and whether it already exists
  const detectedUploadName = uploadGroupFile ? uploadGroupFile.name.replace(/\.(xlsx|xls)$/i, '').trim() : '';
  const uploadNameDuplicate = !!detectedUploadName && groups.some(g => (g.name || '').trim().toUpperCase() === detectedUploadName.toUpperCase());

  // ---- Group selection + Excel download ----
  const toggleGroupSelect = (id) => {
    setSelectedGroupIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const allGroupsSelected = groups.length > 0 && selectedGroupIds.size === groups.length;
  const toggleSelectAllGroups = () => {
    setSelectedGroupIds(allGroupsSelected ? new Set() : new Set(groups.map(g => g.id)));
  };

  const safeFileName = (name) => `${(name || 'grupo').replace(/[\/\\:*?"<>|]/g, '_')}.xlsx`;

  const fetchGroupBlob = async (groupId) => {
    const token = localStorage.getItem('token');
    const res = await axios.get(`${API}/admin/groups/${groupId}/export`, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    });
    return res.data;
  };

  const handleDownloadGroups = async () => {
    const selected = groups.filter(g => selectedGroupIds.has(g.id));
    if (selected.length === 0) { toast.error('Selecciona al menos un grupo'); return; }
    setDownloadingGroups(true);
    try {
      const supportsSave = typeof window.showSaveFilePicker === 'function';
      const supportsDir = typeof window.showDirectoryPicker === 'function';

      if (selected.length === 1 && supportsSave) {
        const g = selected[0];
        let handle;
        try {
          handle = await window.showSaveFilePicker({
            suggestedName: safeFileName(g.name),
            types: [{ description: 'Excel', accept: { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] } }],
          });
        } catch (err) { if (err.name === 'AbortError') return; throw err; }
        const blob = await fetchGroupBlob(g.id);
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
        toast.success('Descarga completada');
      } else if (supportsDir) {
        let dirHandle;
        try { dirHandle = await window.showDirectoryPicker(); }
        catch (err) { if (err.name === 'AbortError') return; throw err; }
        let ok = 0;
        for (const g of selected) {
          const blob = await fetchGroupBlob(g.id);
          const fileHandle = await dirHandle.getFileHandle(safeFileName(g.name), { create: true });
          const writable = await fileHandle.createWritable();
          await writable.write(blob);
          await writable.close();
          ok += 1;
        }
        toast.success(`${ok} archivo(s) descargado(s) en la carpeta seleccionada`);
      } else {
        // Fallback for browsers without the File System Access API (Firefox/Safari)
        for (const g of selected) {
          const blob = await fetchGroupBlob(g.id);
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = safeFileName(g.name);
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(url);
        }
        toast.success('Descarga iniciada (carpeta de Descargas)');
      }
      setSelectedGroupIds(new Set());
    } catch (error) {
      toast.error('Error al descargar los grupos');
    } finally {
      setDownloadingGroups(false);
    }
  };

  const handlePeriodModeChange = async (inst, value) => {
    const opt = PERIOD_MODE_OPTIONS.find(o => o.value === value);
    if (!opt || !opt.enabled) {
      toast.info('Ese modo de periodos aún no está disponible');
      return;
    }
    if ((inst.period_mode || '3_classic') === value) return;
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/institutions/${inst.id}/period-mode`, { period_mode: value }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Modo de periodos actualizado');
      fetchInstitutions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar el modo');
    }
  };

  const openMatriculaDialog = (group) => {
    setMatriculaGroup(group);
    setMatriculaFile(null);
    setMatriculaResult(null);
    if (matriculaFileRef.current) matriculaFileRef.current.value = '';
    setMatriculaDialogOpen(true);
  };

  const handleUpdateMatriculas = async (e) => {
    e.preventDefault();
    if (!matriculaFile || !matriculaGroup) { toast.error('Selecciona un archivo Excel'); return; }
    setUpdatingMatriculas(true);
    setMatriculaResult(null);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', matriculaFile);
      const res = await axios.post(
        `${API}/admin/groups/${matriculaGroup.id}/update-matriculas`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      setMatriculaResult(res.data);
      toast.success(res.data.message || 'Matrículas actualizadas');
      if (selectedInstitution?.id) fetchInstitutionDetails(selectedInstitution.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar matrículas');
    } finally {
      setUpdatingMatriculas(false);
    }
  };


  const handleEditGroupSubmit = async (e) => {
    e.preventDefault();
    if (!editGroupName.trim() || !editingGroupObj) return;
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/groups/${editingGroupObj.id}`, 
        { name: editGroupName, student_names: editingGroupObj.student_names },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Grupo actualizado');
      setEditGroupDialog(false);
      fetchInstitutionDetails(selectedInstitution.id);
    } catch (error) {
      toast.error('Error al actualizar grupo');
    }
  };

  // Change Institution
  const openChangeInstitution = (type, item) => {
    setChangingItem({ type, ...item });
    setNewInstitutionId(item.institution_id || '');
    setChangeInstDialogOpen(true);
  };

  const handleChangeInstitution = async () => {
    try {
      const token = localStorage.getItem('token');
      const endpoint = changingItem.type === 'group' 
        ? `${API}/admin/groups/${changingItem.id}/institution`
        : `${API}/admin/users/${changingItem.id}/institution`;
      
      await axios.put(endpoint, 
        { institution_id: newInstitutionId || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Institución actualizada');
      setChangeInstDialogOpen(false);
      if (selectedInstitution?.id) {
        fetchInstitutionDetails(selectedInstitution.id);
      } else {
        fetchUnassignedData();
      }
      fetchInstitutions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar institución');
    }
  };

  // View teacher classes
  const viewTeacherClasses = async (teacher) => {
    setViewingTeacher(teacher);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/teachers/${teacher.id}/classes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeacherClasses(response.data);
      setClassesDialogOpen(true);
    } catch (error) {
      toast.error('Error al cargar clases');
    }
  };

  if (loading && currentView === 'institutions') {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="p-6 flex items-center justify-center min-h-[60vh]">
          <div className="text-slate-600">Cargando...</div>
        </div>
      </Layout>
    );
  }

  // Institutions List View
  if (currentView === 'institutions') {
    // Count unassigned items
    const unassignedCount = {
      teachers: 0,
      ce: 0,
      groups: 0
    };

    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="p-4 md:p-6 lg:p-8 max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              {firstName(user?.name) && (
                <p className="text-sm text-slate-500 mb-0.5" data-testid="welcome-greeting">
                  Bienvenido(a), <span className="font-semibold text-slate-700">{firstName(user?.name)}</span>
                </p>
              )}
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Panel de Administración</h1>
              <p className="text-sm text-slate-600">Gestión por instituciones</p>
            </div>
            <div className="flex gap-2">
              {isSuperAdmin && (
              <Button
                onClick={toggleMaintenance}
                className={maintenanceMode ? 'bg-amber-500 hover:bg-amber-600' : 'bg-slate-500 hover:bg-slate-600'}
                data-testid="maintenance-toggle"
              >
                {maintenanceMode ? 'Desactivar Mantenimiento' : 'Activar Mantenimiento'}
              </Button>
              )}
              <Dialog open={instDialogOpen} onOpenChange={setInstDialogOpen}>
              {isSuperAdmin && (
              <DialogTrigger asChild>
                <Button className="bg-blue-500 hover:bg-blue-600" data-testid="create-institution-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Nueva Institución
                </Button>
              </DialogTrigger>
              )}
              <DialogContent className="bg-white">
                <DialogHeader>
                  <DialogTitle>Crear Institución</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateInstitution} className="space-y-4">
                  <div>
                    <Label>Nombre de la Institución</Label>
                    <Input
                      value={newInstitution.name}
                      onChange={(e) => setNewInstitution({ name: e.target.value })}
                      placeholder="Ej: Escuela Secundaria #1"
                      required
                      data-testid="institution-name-input"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-blue-500 hover:bg-blue-600">
                    Crear
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
            </div>
          </div>

          {/* Administradores */}
          <Card className="p-4 mb-6 border-l-4 border-purple-500" data-testid="admins-section">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-purple-500" /> Administradores
                </h2>
                <p className="text-xs text-slate-500">Cuentas con acceso al panel administrativo</p>
              </div>
              {isSuperAdmin && (
                <Dialog open={adminDialogOpen} onOpenChange={setAdminDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="bg-purple-500 hover:bg-purple-600" data-testid="create-admin-btn">
                      <Plus className="w-3 h-3 mr-1" /> Nuevo Admin
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-white">
                    <DialogHeader>
                      <DialogTitle>Crear Administrador Secundario</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateAdmin} className="space-y-3">
                      <div>
                        <Label>Nombre</Label>
                        <Input value={newAdmin.name} onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })} required data-testid="new-admin-name" />
                      </div>
                      <div>
                        <Label>Correo</Label>
                        <Input type="email" value={newAdmin.email} onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })} required data-testid="new-admin-email" />
                      </div>
                      <div>
                        <Label>Contraseña inicial (mín. 6 caracteres)</Label>
                        <Input type="text" value={newAdmin.password} onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })} required minLength={6} data-testid="new-admin-password" />
                        <p className="text-xs text-slate-400 mt-1">El admin deberá cambiarla al primer acceso</p>
                      </div>
                      <Button type="submit" disabled={creatingAdmin || !newAdmin.name.trim() || !newAdmin.email.trim() || newAdmin.password.length < 6} className="w-full bg-purple-500 hover:bg-purple-600 disabled:opacity-50" data-testid="new-admin-submit">
                        {creatingAdmin ? 'Creando...' : 'Crear Administrador'}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </div>
            <div className="space-y-2">
              {adminsList.length === 0 ? (
                <p className="text-xs text-slate-400">Sin administradores</p>
              ) : (
                adminsList.map(a => (
                  <div key={a.id} className="flex items-center justify-between p-2 bg-slate-50 rounded-md" data-testid={`admin-row-${a.id}`}>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-purple-700 text-xs font-bold">
                        {(a.name || '?').charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-800 flex items-center gap-2">
                          {a.name} {a.is_me && <span className="text-[10px] text-slate-500">(tú)</span>}
                          {a.is_super_admin && <span className="text-[9px] font-bold uppercase bg-purple-500 text-white px-1.5 py-0.5 rounded">Super</span>}
                        </p>
                        <p className="text-xs text-slate-500">{a.email}</p>
                      </div>
                    </div>
                    {isSuperAdmin && !a.is_super_admin && !a.is_me && (
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => fetchAdminActivity(a.id)} data-testid={`activity-admin-${a.id}`}>
                          Actividad
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleResetAdminPassword(a.id, a.name)} data-testid={`reset-admin-${a.id}`}>
                          Resetear
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs text-red-500 hover:text-red-700" onClick={() => handleDeleteAdmin(a.id, a.name)} data-testid={`delete-admin-${a.id}`}>
                          Eliminar
                        </Button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </Card>

          {/* Activity Dialog */}
          <Dialog open={activityDialog.open} onOpenChange={(o) => setActivityDialog({ ...activityDialog, open: o })}>
            <DialogContent className="bg-white max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Actividad del Administrador</DialogTitle>
              </DialogHeader>
              {activityDialog.loading && <p className="text-sm text-slate-500 py-4">Cargando...</p>}
              {activityDialog.data && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3 text-sm bg-slate-50 p-3 rounded-lg">
                    <div>
                      <p className="text-xs text-slate-500">Nombre</p>
                      <p className="font-medium">{activityDialog.data.admin.name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Correo</p>
                      <p className="font-medium">{activityDialog.data.admin.email}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Creado el</p>
                      <p className="font-medium">{activityDialog.data.admin.created_at ? new Date(activityDialog.data.admin.created_at).toLocaleString('es-MX') : '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Último acceso</p>
                      <p className="font-medium">{activityDialog.data.admin.last_login ? new Date(activityDialog.data.admin.last_login).toLocaleString('es-MX') : 'Nunca ha ingresado'}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs text-slate-500">Total de acciones registradas</p>
                      <p className="font-bold text-purple-600">{activityDialog.data.total_actions}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-semibold mb-2">Historial de acciones (más reciente primero)</p>
                    {activityDialog.data.logs.length === 0 ? (
                      <p className="text-xs text-slate-400 italic">Este administrador aún no ha realizado acciones registradas</p>
                    ) : (
                      <div className="space-y-1 max-h-80 overflow-y-auto border border-slate-200 rounded-lg">
                        {activityDialog.data.logs.map((log, idx) => (
                          <div key={log.id || idx} className="text-xs p-2 border-b last:border-b-0 hover:bg-slate-50">
                            <div className="flex items-center justify-between">
                              <span className="font-medium">
                                <span className="capitalize">{log.action}</span> <span className="text-slate-500">{log.entity_type}</span>: <span className="text-slate-800">{log.entity_name}</span>
                              </span>
                              <span className="text-[10px] text-slate-400">{log.created_at ? new Date(log.created_at).toLocaleString('es-MX') : ''}</span>
                            </div>
                            {log.institution_name && <p className="text-[10px] text-slate-500 mt-0.5">Institución: {log.institution_name}</p>}
                            {log.details && <p className="text-[10px] text-slate-500 mt-0.5">{log.details}</p>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Institution Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {institutions.map(inst => (
              <Card 
                key={inst.id} 
                className={`p-4 cursor-pointer hover:shadow-lg transition-shadow border-l-4 ${
                  inst.is_active === false 
                    ? 'border-l-red-500 bg-red-50/50' 
                    : 'border-l-blue-500'
                }`}
                onClick={() => openInstitutionDetail(inst)}
                data-testid={`institution-card-${inst.id}`}
              >
                {/* Header with icon and name */}
                <div className="flex items-center gap-2 mb-2">
                  <Building2 className={`w-5 h-5 flex-shrink-0 ${inst.is_active === false ? 'text-red-500' : 'text-blue-500'}`} />
                  <h3 className="font-semibold text-slate-900 truncate flex-1">{inst.name}</h3>
                  {inst.is_active === false && (
                    <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded-full flex items-center gap-1">
                      <Lock className="w-3 h-3" />
                      Bloqueada
                    </span>
                  )}
                </div>
                
                {/* Status toggle */}
                <div 
                  className="flex items-center justify-between p-2 mb-3 bg-slate-50 rounded-lg"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center gap-2">
                    {inst.is_active === false ? (
                      <Lock className="w-4 h-4 text-red-500" />
                    ) : (
                      <Unlock className="w-4 h-4 text-green-500" />
                    )}
                    <span className="text-sm text-slate-600">
                      {inst.is_active === false ? 'Acceso bloqueado' : 'Acceso activo'}
                    </span>
                  </div>
                  <button
                    type="button"
                    data-testid={`toggle-status-${inst.id}`}
                    disabled={!isSuperAdmin}
                    title={!isSuperAdmin ? 'Solo el Super Administrador puede cambiar este estado' : ''}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isSuperAdmin) return;
                      handleToggleInstitutionStatus(inst);
                    }}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      inst.is_active !== false ? 'bg-green-500' : 'bg-gray-300'
                    } ${!isSuperAdmin ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-lg transition-transform ${
                        inst.is_active !== false ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Period mode selector (per institution) */}
                <div
                  className="mb-3"
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`period-mode-wrap-${inst.id}`}
                >
                  <label className="text-xs text-slate-500 mb-1 block">Modo de periodos</label>
                  <Select
                    value={inst.period_mode || '3_classic'}
                    onValueChange={(v) => handlePeriodModeChange(inst, v)}
                  >
                    <SelectTrigger className="h-9 text-sm" data-testid={`period-mode-select-${inst.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {PERIOD_MODE_OPTIONS.map(opt => (
                        <SelectItem
                          key={opt.value}
                          value={opt.value}
                          disabled={!opt.enabled}
                          data-testid={`period-mode-option-${opt.value}`}
                        >
                          {opt.label}{!opt.enabled ? ' — próximamente' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Action buttons row */}
                <div className="flex justify-end gap-2 mb-3">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 px-2 text-slate-600 hover:text-slate-800"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingInstitution(inst);
                      setEditInstDialogOpen(true);
                    }}
                  >
                    <Pencil className="w-3 h-3 mr-1" />
                    Editar
                  </Button>
                  {isSuperAdmin && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 px-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteInstitution(inst.id);
                    }}
                  >
                    <Trash2 className="w-3 h-3 mr-1" />
                    Eliminar
                  </Button>
                  )}
                </div>
                
                {/* Stats grid */}
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div className="flex items-center gap-1.5 text-slate-600">
                    <Users className="w-3.5 h-3.5 text-blue-500" />
                    <span>{inst.teacher_count} Profesores</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-slate-600">
                    <Shield className="w-3.5 h-3.5 text-teal-500" />
                    <span>{inst.control_escolar_count} C. Escolar</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-slate-600">
                    <Compass className="w-3.5 h-3.5 text-purple-500" />
                    <span>{inst.orientador_count || 0} Orientadores</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-slate-600">
                    <BookOpen className="w-3.5 h-3.5 text-green-500" />
                    <span>{inst.group_count} Grupos</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-slate-600">
                    <BookOpen className="w-3.5 h-3.5 text-orange-500" />
                    <span>{inst.class_count} Clases</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-slate-700 font-medium">
                    <Users className="w-3.5 h-3.5 text-indigo-500" />
                    <span>{inst.total_students || 0} Alumnos</span>
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-end text-blue-500 text-sm">
                  Ver detalles <ChevronRight className="w-4 h-4" />
                </div>
              </Card>
            ))}

            {/* "Sin Institución" card */}
            <Card 
              className="p-4 cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-l-slate-400 bg-slate-50"
              onClick={() => openInstitutionDetail(null)}
              data-testid="unassigned-card"
            >
              <div className="flex items-center gap-2 mb-3">
                <Building2 className="w-5 h-5 text-slate-400" />
                <h3 className="font-semibold text-slate-700">Sin Institución Asignada</h3>
              </div>
              <p className="text-sm text-slate-500 mb-3">
                Profesores, grupos y usuarios sin institución asignada
              </p>
              <div className="flex items-center justify-end text-slate-500 text-sm">
                Ver y asignar <ChevronRight className="w-4 h-4" />
              </div>
            </Card>
          </div>

          {institutions.length === 0 && (
            <Card className="p-8 text-center bg-slate-50 mt-4">
              <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <h3 className="font-semibold text-slate-700 mb-2">Sin instituciones</h3>
              <p className="text-slate-500 text-sm">Crea tu primera institución para organizar profesores y grupos</p>
            </Card>
          )}

          {/* Audit Log Section */}
          <div className="mt-6">
            <Button
              variant="outline"
              onClick={() => { setShowAuditLog(!showAuditLog); if (!showAuditLog && auditLog.length === 0) fetchAuditLog(); }}
              className="border-slate-300 text-slate-600 hover:bg-slate-50"
              data-testid="toggle-audit-log-btn"
            >
              <Clock className="w-4 h-4 mr-2" />
              {showAuditLog ? 'Ocultar' : 'Ver'} Historial de Acciones
            </Button>
            {showAuditLog && (
              <Card className="mt-3 overflow-hidden border-slate-200">
                <div className="p-4 bg-gradient-to-r from-slate-50 to-gray-50 border-b border-slate-200 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-slate-600" />
                    <span className="font-semibold text-slate-900">Historial de Acciones del Administrador</span>
                  </div>
                  <Button size="sm" variant="outline" onClick={fetchAuditLog} disabled={loadingAuditLog}>
                    <RefreshIcon className={`w-4 h-4 mr-1 ${loadingAuditLog ? 'animate-spin' : ''}`} />
                    Actualizar
                  </Button>
                </div>
                <div className="p-4">
                  {loadingAuditLog ? (
                    <div className="text-center py-6 text-slate-500">Cargando...</div>
                  ) : auditLog.length === 0 ? (
                    <div className="text-center py-6 text-slate-500">
                      <Clock className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                      <p>No hay acciones registradas</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                      {auditLog.map((log) => {
                        const actionColors = {
                          'crear': 'text-green-700 bg-green-50 border-green-200',
                          'eliminar': 'text-red-700 bg-red-50 border-red-200',
                          'borrado masivo': 'text-red-700 bg-red-50 border-red-200',
                          'bloquear': 'text-orange-700 bg-orange-50 border-orange-200',
                          'activar': 'text-blue-700 bg-blue-50 border-blue-200',
                        };
                        const color = actionColors[log.action] || 'text-slate-700 bg-slate-50 border-slate-200';
                        return (
                          <div key={log.id} className={`p-3 rounded-lg border ${color}`} data-testid={`audit-log-${log.id}`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-bold uppercase px-2 py-0.5 rounded-full bg-white border">
                                  {log.action}
                                </span>
                                <span className="text-sm font-medium">{log.entity_type}</span>
                                <span className="text-sm">—</span>
                                <span className="text-sm font-semibold">{log.entity_name}</span>
                              </div>
                              <span className="text-xs text-slate-500">
                                {new Date(log.created_at).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                            <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                              {log.institution_name && <span>Institución: {log.institution_name}</span>}
                              {log.details && <span>| {log.details}</span>}
                              <span>| Por: {log.admin_name}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </Card>
            )}
          </div>
        </div>

        {/* Edit Institution Dialog */}
        <Dialog open={editInstDialogOpen} onOpenChange={setEditInstDialogOpen}>
          <DialogContent className="bg-white">
            <DialogHeader>
              <DialogTitle>Editar Institución</DialogTitle>
            </DialogHeader>
            {editingInstitution && (
              <form onSubmit={handleEditInstitution} className="space-y-4">
                <div>
                  <Label>Nombre</Label>
                  <Input
                    value={editingInstitution.name}
                    onChange={(e) => setEditingInstitution({ ...editingInstitution, name: e.target.value })}
                    required
                  />
                </div>
                <Button type="submit" className="w-full bg-blue-500 hover:bg-blue-600">
                  Guardar
                </Button>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </Layout>
    );
  }

  // Institution Detail View
  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-4 md:p-6 lg:p-8 max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button variant="ghost" onClick={goBackToInstitutions} className="mb-2">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver a instituciones
          </Button>
          <div className="flex items-center gap-3">
            <Building2 className="w-8 h-8 text-blue-500" />
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{selectedInstitution?.name}</h1>
              <p className="text-sm text-slate-600">Gestión de roles y grupos</p>
            </div>
          </div>

          {/* Registros de acceso de esta institución */}
          <div className="flex flex-wrap gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => navigate(`/teacher-access?inst=${selectedInstitution?.id}`)}
              disabled={!selectedInstitution?.id}
              data-testid="institution-teacher-access-btn"
              className="border-indigo-200 text-indigo-600 hover:bg-indigo-50"
            >
              <Users className="w-4 h-4 mr-2" />
              Acceso de Profesores
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate(`/ti-access?inst=${selectedInstitution?.id}`)}
              disabled={!selectedInstitution?.id}
              data-testid="institution-ti-access-btn"
              className="border-indigo-200 text-indigo-600 hover:bg-indigo-50"
            >
              <Users className="w-4 h-4 mr-2" />
              Acceso de Tutores de Grupo
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="p-4 bg-blue-50">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              <span className="text-2xl font-bold">{teachers.length}</span>
            </div>
            <p className="text-sm text-slate-600">Profesores</p>
          </Card>
          <Card className="p-4 bg-purple-50">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-purple-600" />
              <span className="text-2xl font-bold">{controlEscolarUsers.length}</span>
            </div>
            <p className="text-sm text-slate-600">Control Escolar</p>
          </Card>
          <Card className="p-4 bg-cyan-50">
            <div className="flex items-center gap-2">
              <Compass className="w-5 h-5 text-cyan-600" />
              <span className="text-2xl font-bold">{orientadores.length}</span>
            </div>
            <p className="text-sm text-slate-600">Orientadores</p>
          </Card>
          <Card className="p-4 bg-green-50">
            <div className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-green-600" />
              <span className="text-2xl font-bold">{groups.length}</span>
            </div>
            <p className="text-sm text-slate-600">Grupos</p>
          </Card>
        </div>

        {/* Teachers Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-500" />
              Profesores
            </h2>
            <Button onClick={() => setTeacherDialogOpen(true)} className="bg-blue-500 hover:bg-blue-600" size="sm">
              <UserPlus className="w-4 h-4 mr-1" /> Agregar
            </Button>
          </div>
          
          {teachers.length === 0 ? (
            <Card className="p-4 text-center bg-slate-50 text-slate-500">
              No hay profesores en esta institución
            </Card>
          ) : (
            <div className="space-y-2">
              {teachers.map(teacher => (
                <Card key={teacher.id} className="p-3 flex items-center justify-between">
                  <div>
                    <p className="font-medium flex items-center gap-2">
                      <span
                        className={`inline-block w-2.5 h-2.5 rounded-full ${onlineTeachers.has(teacher.id) ? 'bg-green-500 animate-pulse' : 'bg-slate-300'}`}
                        title={onlineTeachers.has(teacher.id) ? 'En línea ahora' : 'Desconectado'}
                        data-testid={`teacher-presence-${teacher.id}`}
                      />
                      {teacher.name}
                    </p>
                    <p className="text-sm text-slate-500 flex items-center gap-1">
                      <Mail className="w-3 h-3" /> {teacher.email}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => viewTeacherClasses(teacher)} title="Ver clases">
                      <BookOpen className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => {
                      setEditingTeacher(teacher);
                      setEditTeacherForm({ name: teacher.name, email: teacher.email });
                      setEditTeacherDialogOpen(true);
                    }} title="Editar">
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => {
                      setResetUser({ id: teacher.id, name: teacher.name, role: 'teacher' });
                      setResetDialogOpen(true);
                    }} title="Resetear contraseña">
                      <Key className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openChangeInstitution('user', teacher)} 
                      className="text-orange-500" title="Cambiar institución">
                      <Building2 className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteTeacher(teacher.id)} 
                      className="text-red-500" title="Eliminar" data-testid={`delete-teacher-${teacher.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Control Escolar Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Shield className="w-5 h-5 text-purple-500" />
              Control Escolar
            </h2>
            <Button onClick={() => setCeDialogOpen(true)} className="bg-purple-500 hover:bg-purple-600" size="sm">
              <UserPlus className="w-4 h-4 mr-1" /> Agregar
            </Button>
          </div>
          
          {controlEscolarUsers.length === 0 ? (
            <Card className="p-4 text-center bg-slate-50 text-slate-500">
              No hay usuarios de Control Escolar en esta institución
            </Card>
          ) : (
            <div className="space-y-2">
              {controlEscolarUsers.map(ce => (
                <Card key={ce.id} className="p-3 flex items-center justify-between">
                  <div>
                    <p className="font-medium">{ce.name}</p>
                    <p className="text-sm text-slate-500 flex items-center gap-1">
                      <Mail className="w-3 h-3" /> {ce.email}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" 
                      onClick={() => {
                        setResetUser({ id: ce.id, name: ce.name, role: 'control_escolar' });
                        setResetDialogOpen(true);
                      }} 
                      className="text-blue-500" title="Resetear contraseña"
                      data-testid={`reset-password-ce-${ce.id}`}>
                      <Key className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openChangeInstitution('user', ce)} 
                      className="text-orange-500" title="Cambiar institución">
                      <Building2 className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openDeleteCEDialog(ce)} 
                      className="text-red-500" title="Eliminar">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Orientadores Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Compass className="w-5 h-5 text-cyan-500" />
              Orientadores
            </h2>
            <Button onClick={() => setOrientadorDialogOpen(true)} className="bg-cyan-500 hover:bg-cyan-600" size="sm">
              <UserPlus className="w-4 h-4 mr-1" /> Agregar
            </Button>
          </div>
          
          {orientadores.length === 0 ? (
            <Card className="p-4 text-center bg-slate-50 text-slate-500">
              No hay orientadores en esta institución
            </Card>
          ) : (
            <div className="space-y-2">
              {orientadores.map(orientador => (
                <Card key={orientador.id} className="p-3 flex items-center justify-between">
                  <div>
                    <p className="font-medium">{orientador.name}</p>
                    <p className="text-sm text-slate-500 flex items-center gap-1">
                      <Mail className="w-3 h-3" /> {orientador.email}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" 
                      onClick={() => {
                        setResetUser({ id: orientador.id, name: orientador.name, role: 'orientador' });
                        setResetDialogOpen(true);
                      }} 
                      className="text-blue-500" title="Resetear contraseña"
                      data-testid={`reset-password-orientador-${orientador.id}`}>
                      <Key className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openDeleteOrientadorDialog(orientador)} 
                      className="text-red-500" title="Eliminar">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Prefectura Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-red-500" />
              Prefectura
            </h2>
            <Button onClick={() => setPrefectoDialogOpen(true)} className="bg-red-500 hover:bg-red-600" size="sm" data-testid="add-prefecto-btn">
              <UserPlus className="w-4 h-4 mr-1" /> Agregar
            </Button>
          </div>

          {prefectos.length === 0 ? (
            <Card className="p-4 text-center bg-slate-50 text-slate-500">
              No hay prefectos en esta institución
            </Card>
          ) : (
            <div className="space-y-2">
              {prefectos.map(prefecto => (
                <Card key={prefecto.id} className="p-3 flex items-center justify-between" data-testid={`prefecto-card-${prefecto.id}`}>
                  <div>
                    <p className="font-medium">{prefecto.name}</p>
                    <p className="text-sm text-slate-500 flex items-center gap-1">
                      <Mail className="w-3 h-3" /> {prefecto.email}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm"
                      onClick={() => {
                        setResetUser({ id: prefecto.id, name: prefecto.name, role: 'prefectura' });
                        setResetDialogOpen(true);
                      }}
                      className="text-blue-500" title="Resetear contraseña"
                      data-testid={`reset-password-prefecto-${prefecto.id}`}>
                      <Key className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openDeletePrefectoDialog(prefecto)}
                      className="text-red-500" title="Eliminar"
                      data-testid={`delete-prefecto-${prefecto.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Subdirección Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Compass className="w-5 h-5 text-sky-500" />
              Subdirección
            </h2>
            <Button onClick={() => setSubdirectorDialogOpen(true)} className="bg-sky-500 hover:bg-sky-600" size="sm" data-testid="add-subdirector-btn">
              <UserPlus className="w-4 h-4 mr-1" /> Agregar
            </Button>
          </div>

          {subdirectores.length === 0 ? (
            <Card className="p-4 text-center bg-slate-50 text-slate-500">
              No hay subdirectores en esta institución
            </Card>
          ) : (
            <div className="space-y-2">
              {subdirectores.map(subdirector => (
                <Card key={subdirector.id} className="p-3 flex items-center justify-between" data-testid={`subdirector-card-${subdirector.id}`}>
                  <div>
                    <p className="font-medium">{subdirector.name}</p>
                    <p className="text-sm text-slate-500 flex items-center gap-1">
                      <Mail className="w-3 h-3" /> {subdirector.email}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm"
                      onClick={() => {
                        setResetUser({ id: subdirector.id, name: subdirector.name, role: 'subdirector' });
                        setResetDialogOpen(true);
                      }}
                      className="text-blue-500" title="Resetear contraseña"
                      data-testid={`reset-password-subdirector-${subdirector.id}`}>
                      <Key className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openDeleteSubdirectorDialog(subdirector)}
                      className="text-red-500" title="Eliminar"
                      data-testid={`delete-subdirector-${subdirector.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Groups Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Shield className="w-5 h-5 text-indigo-500" />
              Jefe de Docentes
            </h2>
            <Button onClick={() => setJdDialogOpen(true)} className="bg-indigo-500 hover:bg-indigo-600" size="sm">
              <UserPlus className="w-4 h-4 mr-1" /> Agregar
            </Button>
          </div>
          
          {jefeDocentes.length === 0 ? (
            <Card className="p-4 text-center bg-slate-50 text-slate-500">
              No hay Jefe de Docentes en esta institución
            </Card>
          ) : (
            <div className="space-y-2">
              {jefeDocentes.map(jd => (
                <Card key={jd.id} className="p-3 flex items-center justify-between">
                  <div>
                    <p className="font-medium">{jd.name}</p>
                    <p className="text-sm text-slate-500 flex items-center gap-1">
                      <Mail className="w-3 h-3" /> {jd.email}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" 
                      onClick={() => { setResetUser({ id: jd.id, name: jd.name, role: 'jefe_docentes' }); setResetDialogOpen(true); }} 
                      className="text-blue-500" title="Resetear contraseña">
                      <Key className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteJefeDocentes(jd.id)} 
                      className="text-red-500" title="Eliminar">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Jefe Tutores Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Shield className="w-5 h-5 text-teal-500" />
              Jefe de Tutores
            </h2>
            <Button onClick={() => setJtDialogOpen(true)} className="bg-teal-500 hover:bg-teal-600" size="sm">
              <UserPlus className="w-4 h-4 mr-1" /> Agregar
            </Button>
          </div>
          {jefeTutores.length === 0 ? (
            <Card className="p-4 text-center bg-slate-50 text-slate-500">No hay Jefe de Tutores en esta institución</Card>
          ) : (
            <div className="space-y-2">
              {jefeTutores.map(jt => (
                <Card key={jt.id} className="p-3 flex items-center justify-between">
                  <div>
                    <p className="font-medium">{jt.name}</p>
                    <p className="text-sm text-slate-500 flex items-center gap-1"><Mail className="w-3 h-3" /> {jt.email}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => { setResetUser({ id: jt.id, name: jt.name, role: 'jefe_tutores' }); setResetDialogOpen(true); }} className="text-blue-500" title="Resetear contraseña"><Key className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteJefeTutores(jt.id)} className="text-red-500" title="Eliminar"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Groups Section (original) */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Users className="w-5 h-5 text-green-500" />
              Grupos de Alumnos
            </h2>
            <div className="flex items-center gap-2">
              <Button
                className="bg-green-500 hover:bg-green-600 text-white"
                size="sm"
                onClick={() => setUploadGroupDialogOpen(true)}
                data-testid="upload-group-btn"
              >
                <Upload className="w-4 h-4 mr-1" />
                Importar Grupo
              </Button>
              {groups.length > 0 && (
                <Button
                  className="bg-blue-500 hover:bg-blue-600 text-white disabled:opacity-50"
                  size="sm"
                  onClick={handleDownloadGroups}
                  disabled={selectedGroupIds.size === 0 || downloadingGroups}
                  data-testid="download-groups-btn"
                >
                  <Download className="w-4 h-4 mr-1" />
                  {downloadingGroups ? 'Descargando...' : `Descargar${selectedGroupIds.size > 0 ? ` (${selectedGroupIds.size})` : ''}`}
                </Button>
              )}
              {groups.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setScaleGroupsInstId(selectedInstitution.id); setScaleGroupsDialogOpen(true); }}
                  className="border-violet-300 text-violet-600 hover:bg-violet-50 hover:text-violet-700"
                  data-testid="scale-groups-btn"
                >
                  <ArrowUpCircle className="w-4 h-4 mr-1" />
                  Escalar grupos
                </Button>
              )}
            </div>
          </div>

          {groups.length > 0 && (
            <div className="flex items-center gap-2 mb-3 pl-1">
              <Checkbox
                id="select-all-groups"
                checked={allGroupsSelected}
                onCheckedChange={toggleSelectAllGroups}
                data-testid="select-all-groups-checkbox"
              />
              <label htmlFor="select-all-groups" className="text-xs text-slate-600 cursor-pointer select-none">
                Seleccionar todos ({selectedGroupIds.size}/{groups.length})
              </label>
            </div>
          )}
          
          {groups.length === 0 ? (
            <Card className="p-4 text-center bg-slate-50 text-slate-500">
              <Users className="w-10 h-10 mx-auto mb-2 text-slate-300" />
              <p>No hay grupos en esta institución. Importa uno desde Excel.</p>
            </Card>
          ) : (
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {groups.map(group => (
                <Card key={group.id} className={`p-4 bg-white border rounded-xl shadow-sm hover:shadow-md transition-all ${selectedGroupIds.has(group.id) ? 'border-blue-400 ring-1 ring-blue-200' : 'border-slate-100'}`} data-testid={`group-card-${group.id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={selectedGroupIds.has(group.id)}
                        onCheckedChange={() => toggleGroupSelect(group.id)}
                        data-testid={`group-select-${group.id}`}
                      />
                      <div className="bg-green-100 w-8 h-8 rounded-full flex items-center justify-center">
                        <Users className="w-4 h-4 text-green-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900 text-sm">{group.name}</h3>
                        <p className="text-xs text-slate-500">{group.student_names?.length || 0} alumnos</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={() => { setViewingGroup(group); setViewGroupDialog(true); }}>
                      Ver Alumnos
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openMatriculaDialog(group)} className="text-blue-500" title="Actualizar matrícula (Excel)" data-testid={`update-matricula-${group.id}`}>
                      <FileSpreadsheet className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => { setEditingGroupObj(group); setEditGroupName(group.name); setEditGroupDialog(true); }} title="Editar nombre">
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openChangeInstitution('group', group)} className="text-orange-500" title="Cambiar institución">
                      <Building2 className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteGroup(group.id, group.name)} className="text-red-500" title="Eliminar grupo">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Classes Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-purple-500" />
              Clases de la Institución
            </h3>
            <div className="flex items-center gap-3 flex-wrap">
              {institutionClasses.length > 0 && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setNotifyProfsInstId(selectedInstitution.id); setNotifyProfsDialogOpen(true); }}
                    className="border-amber-300 text-amber-600 hover:bg-amber-50 hover:text-amber-700"
                    data-testid="notify-profs-btn"
                  >
                    <Megaphone className="w-4 h-4 mr-1" />
                    Avisar profesores
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDeleteAllClassesDialogOpen(true)}
                    className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                    data-testid="delete-all-classes-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Borrar todas las clases
                  </Button>
                </>
              )}
              <span className="text-sm text-gray-500">
                {loadingClasses ? 'Cargando...' : `${institutionClasses.length} clases`}
              </span>
            </div>
          </div>
          
          {loadingClasses ? (
            <div className="text-center py-4 text-gray-500">Cargando clases...</div>
          ) : institutionClasses.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No hay clases en esta institución</p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {institutionClasses.map(cls => (
                <div key={cls.id} className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-100">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-purple-900">{cls.subject}</span>
                      <span className="text-xs bg-purple-200 text-purple-800 px-2 py-0.5 rounded-full">
                        {cls.group}
                      </span>
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      <span>Profesor: {cls.teacher_name}</span>
                      <span className="mx-2">•</span>
                      <span>{cls.student_count} alumnos</span>
                      <span className="mx-2">•</span>
                      <span>{cls.tutor_count} tutores</span>
                      {cls.access_code && (
                        <>
                          <span className="mx-2">•</span>
                          <span className="font-mono bg-gray-200 px-1 rounded">{cls.access_code}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditClassDialog(cls)}
                      className="text-blue-500"
                      title="Editar clase"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openDeleteClassDialog(cls)}
                      className="text-red-500"
                      title="Eliminar clase"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create Teacher Dialog */}
      <Dialog open={teacherDialogOpen} onOpenChange={setTeacherDialogOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Agregar Profesor</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateTeacher} className="space-y-4">
            <div>
              <Label>Nombre Completo</Label>
              <OptimizedInput
                value={newTeacher.name}
                onChange={(e) => setNewTeacher({ ...newTeacher, name: e.target.value })}
                debounceMs={100}
                required
              />
            </div>
            <div>
              <Label>Correo Electrónico</Label>
              <OptimizedInput
                type="email"
                value={newTeacher.email}
                onChange={(e) => setNewTeacher({ ...newTeacher, email: e.target.value })}
                debounceMs={100}
                required
              />
            </div>
            <div>
              <Label>Contraseña</Label>
              <OptimizedInput
                type="password"
                value={newTeacher.password}
                onChange={(e) => setNewTeacher({ ...newTeacher, password: e.target.value })}
                debounceMs={100}
                required
              />
            </div>
            <Button type="submit" className="w-full bg-blue-500 hover:bg-blue-600">
              Crear Profesor
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Teacher Dialog */}
      <Dialog open={editTeacherDialogOpen} onOpenChange={setEditTeacherDialogOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Editar Profesor</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditTeacher} className="space-y-4">
            <div>
              <Label>Nombre</Label>
              <Input
                value={editTeacherForm.name}
                onChange={(e) => setEditTeacherForm({ ...editTeacherForm, name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Correo</Label>
              <Input
                type="email"
                value={editTeacherForm.email}
                onChange={(e) => setEditTeacherForm({ ...editTeacherForm, email: e.target.value })}
                required
              />
            </div>
            <Button type="submit" className="w-full bg-blue-500 hover:bg-blue-600">
              Guardar
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Control Escolar Dialog */}
      <Dialog open={ceDialogOpen} onOpenChange={setCeDialogOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Agregar Control Escolar</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateCE} className="space-y-4">
            <div>
              <Label>Nombre Completo</Label>
              <Input
                value={newCE.name}
                onChange={(e) => setNewCE({ ...newCE, name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Correo Electrónico</Label>
              <Input
                type="email"
                value={newCE.email}
                onChange={(e) => setNewCE({ ...newCE, email: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Contraseña</Label>
              <Input
                type="password"
                value={newCE.password}
                onChange={(e) => setNewCE({ ...newCE, password: e.target.value })}
                required
              />
            </div>
            <Button type="submit" className="w-full bg-purple-500 hover:bg-purple-600">
              Crear Usuario
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Orientador Dialog */}
      <Dialog open={orientadorDialogOpen} onOpenChange={setOrientadorDialogOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Agregar Orientador</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateOrientador} className="space-y-4">
            <div>
              <Label>Nombre Completo</Label>
              <Input
                value={newOrientador.name}
                onChange={(e) => setNewOrientador({ ...newOrientador, name: e.target.value })}
                required
                data-testid="orientador-name-input"
              />
            </div>
            <div>
              <Label>Correo Electrónico</Label>
              <Input
                type="email"
                value={newOrientador.email}
                onChange={(e) => setNewOrientador({ ...newOrientador, email: e.target.value })}
                required
                data-testid="orientador-email-input"
              />
            </div>
            <div>
              <Label>Contraseña</Label>
              <Input
                type="password"
                value={newOrientador.password}
                onChange={(e) => setNewOrientador({ ...newOrientador, password: e.target.value })}
                required
                data-testid="orientador-password-input"
              />
            </div>
            <Button type="submit" className="w-full bg-cyan-500 hover:bg-cyan-600" data-testid="create-orientador-btn">
              Crear Orientador
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Prefecto Dialog */}
      <Dialog open={prefectoDialogOpen} onOpenChange={setPrefectoDialogOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Agregar Prefecto</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreatePrefecto} className="space-y-4">
            <div>
              <Label>Nombre Completo</Label>
              <Input
                value={newPrefecto.name}
                onChange={(e) => setNewPrefecto({ ...newPrefecto, name: e.target.value })}
                required
                data-testid="prefecto-name-input"
              />
            </div>
            <div>
              <Label>Correo Electrónico</Label>
              <Input
                type="email"
                value={newPrefecto.email}
                onChange={(e) => setNewPrefecto({ ...newPrefecto, email: e.target.value })}
                required
                data-testid="prefecto-email-input"
              />
            </div>
            <div>
              <Label>Contraseña</Label>
              <Input
                type="password"
                value={newPrefecto.password}
                onChange={(e) => setNewPrefecto({ ...newPrefecto, password: e.target.value })}
                required
                data-testid="prefecto-password-input"
              />
            </div>
            <Button type="submit" className="w-full bg-red-500 hover:bg-red-600" data-testid="create-prefecto-btn">
              Crear Prefecto
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Subdirector Dialog */}
      <Dialog open={subdirectorDialogOpen} onOpenChange={setSubdirectorDialogOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Agregar Subdirector</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateSubdirector} className="space-y-4">
            <div>
              <Label>Nombre Completo</Label>
              <Input
                value={newSubdirector.name}
                onChange={(e) => setNewSubdirector({ ...newSubdirector, name: e.target.value })}
                required
                data-testid="subdirector-name-input"
              />
            </div>
            <div>
              <Label>Correo Electrónico</Label>
              <Input
                type="email"
                value={newSubdirector.email}
                onChange={(e) => setNewSubdirector({ ...newSubdirector, email: e.target.value })}
                required
                data-testid="subdirector-email-input"
              />
            </div>
            <div>
              <Label>Contraseña</Label>
              <Input
                type="password"
                value={newSubdirector.password}
                onChange={(e) => setNewSubdirector({ ...newSubdirector, password: e.target.value })}
                required
                data-testid="subdirector-password-input"
              />
            </div>
            <Button type="submit" className="w-full bg-sky-500 hover:bg-sky-600" data-testid="create-subdirector-btn">
              Crear Subdirector
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Jefe Docentes Dialog */}
      <Dialog open={jdDialogOpen} onOpenChange={setJdDialogOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Agregar Jefe de Docentes</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateJefeDocentes} className="space-y-4">
            <div>
              <Label>Nombre Completo</Label>
              <Input value={newJD.name} onChange={(e) => setNewJD({ ...newJD, name: e.target.value })} required data-testid="jd-name-input" />
            </div>
            <div>
              <Label>Correo Electrónico</Label>
              <Input type="email" value={newJD.email} onChange={(e) => setNewJD({ ...newJD, email: e.target.value })} required data-testid="jd-email-input" />
            </div>
            <div>
              <Label>Contraseña</Label>
              <Input type="password" value={newJD.password} onChange={(e) => setNewJD({ ...newJD, password: e.target.value })} required data-testid="jd-password-input" />
            </div>
            <Button type="submit" className="w-full bg-indigo-500 hover:bg-indigo-600" data-testid="create-jd-btn">
              Crear Jefe de Docentes
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Jefe Tutores Dialog */}
      <Dialog open={jtDialogOpen} onOpenChange={setJtDialogOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Agregar Jefe de Tutores</DialogTitle></DialogHeader>
          <form onSubmit={handleCreateJefeTutores} className="space-y-4">
            <div><Label>Nombre Completo</Label><Input value={newJT.name} onChange={(e) => setNewJT({ ...newJT, name: e.target.value })} required data-testid="jt-name-input" /></div>
            <div><Label>Correo Electrónico</Label><Input type="email" value={newJT.email} onChange={(e) => setNewJT({ ...newJT, email: e.target.value })} required data-testid="jt-email-input" /></div>
            <div><Label>Contraseña</Label><Input type="password" value={newJT.password} onChange={(e) => setNewJT({ ...newJT, password: e.target.value })} required data-testid="jt-password-input" /></div>
            <Button type="submit" className="w-full bg-teal-500 hover:bg-teal-600" data-testid="create-jt-btn">Crear Jefe de Tutores</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Resetear Contraseña</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-4">
            <p className="text-sm text-slate-600">
              Nueva contraseña para: <strong>{resetUser?.name}</strong>
            </p>
            <div>
              <Label>Nueva Contraseña</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
            </div>
            <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600">
              Resetear
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Change Institution Dialog */}
      <Dialog open={changeInstDialogOpen} onOpenChange={setChangeInstDialogOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Cambiar Institución</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Selecciona la nueva institución para: <strong>{changingItem?.name}</strong>
            </p>
            <Select value={newInstitutionId || "none"} onValueChange={(val) => setNewInstitutionId(val === "none" ? "" : val)}>
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar institución" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin Institución</SelectItem>
                {institutions.map(inst => (
                  <SelectItem key={inst.id} value={inst.id}>{inst.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setChangeInstDialogOpen(false)}>
                Cancelar
              </Button>
              <Button className="flex-1 bg-orange-500 hover:bg-orange-600" onClick={handleChangeInstitution}>
                Cambiar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Classes Dialog */}
      <Dialog open={classesDialogOpen} onOpenChange={setClassesDialogOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Clases de {viewingTeacher?.name}</DialogTitle>
          </DialogHeader>
          {teacherClasses.length === 0 ? (
            <p className="text-slate-500 text-center py-4">Este profesor no tiene clases creadas</p>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {teacherClasses.map(cls => (
                <Card key={cls.id} className="p-3">
                  <p className="font-medium">{cls.subject}</p>
                  <p className="text-sm text-slate-500">Grupo: {cls.group}</p>
                  <p className="text-xs text-slate-400">{cls.student_count} alumnos, {cls.tutor_count} tutores</p>
                </Card>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Teacher Confirmation Dialog */}
      <Dialog open={deleteTeacherDialogOpen} onOpenChange={setDeleteTeacherDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Confirmar eliminación
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-gray-700">
              El profesor <strong>{teacherToDelete?.name}</strong> tiene{' '}
              <strong>{teacherClassesToDelete.length}</strong> clase(s) asociada(s):
            </p>
            <div className="max-h-40 overflow-y-auto bg-gray-50 rounded-lg p-3">
              <ul className="space-y-1">
                {teacherClassesToDelete.map((cls, idx) => (
                  <li key={cls.id || idx} className="text-sm text-gray-600 flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-gray-400" />
                    {cls.subject}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                <strong>⚠️ Advertencia:</strong> Al eliminar el profesor, también se eliminarán todas sus clases, 
                incluyendo registros de asistencia, tareas y vínculos con tutores.
              </p>
            </div>
            <div className="flex gap-3 pt-2">
              <Button 
                variant="outline" 
                onClick={handleCancelDeleteTeacher}
                className="flex-1"
                disabled={deletingTeacher}
              >
                Cancelar
              </Button>
              <Button 
                variant="destructive"
                onClick={handleConfirmDeleteTeacherWithClasses}
                className="flex-1 bg-red-600 hover:bg-red-700"
                disabled={deletingTeacher}
              >
                {deletingTeacher ? 'Eliminando...' : 'Eliminar todo'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Group Confirmation Dialog */}
      <Dialog open={deleteGroupDialogOpen} onOpenChange={setDeleteGroupDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Confirmar eliminación de grupo
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {groupHasLinkedClasses ? (
              <>
                <p className="text-gray-700">
                  El grupo <strong>{groupToDelete?.name}</strong> tiene{' '}
                  <strong>{groupClassesToDelete.length}</strong> clase(s) vinculada(s):
                </p>
                <div className="max-h-40 overflow-y-auto bg-gray-50 rounded-lg p-3">
                  <ul className="space-y-1">
                    {groupClassesToDelete.map((cls, idx) => (
                      <li key={cls.id || idx} className="text-sm text-gray-600 flex items-center gap-2">
                        <BookOpen className="w-4 h-4 text-gray-400" />
                        {cls.subject}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm text-yellow-800">
                    <strong>⚠️ Advertencia:</strong> Al eliminar el grupo, también se eliminarán todas las clases vinculadas, 
                    incluyendo registros de asistencia, tareas y vínculos con tutores.
                  </p>
                </div>
              </>
            ) : (
              <>
                <p className="text-gray-700">
                  ¿Está seguro de que desea eliminar el grupo <strong>{groupToDelete?.name}</strong>?
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    Este grupo no tiene clases vinculadas. La eliminación solo afectará al grupo.
                  </p>
                </div>
              </>
            )}
            <div className="flex gap-3 pt-2">
              <Button 
                variant="outline" 
                onClick={handleCancelDeleteGroup}
                className="flex-1"
                disabled={deletingGroup}
              >
                Cancelar
              </Button>
              <Button 
                variant="destructive"
                onClick={handleConfirmDeleteGroupWithClasses}
                className="flex-1 bg-red-600 hover:bg-red-700"
                disabled={deletingGroup}
                data-testid="confirm-delete-group-btn"
              >
                {deletingGroup ? 'Eliminando...' : (groupHasLinkedClasses ? 'Eliminar todo' : 'Eliminar grupo')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Class Dialog */}
      <Dialog open={editClassDialogOpen} onOpenChange={setEditClassDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-500" />
              Editar Clase
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Asignatura</Label>
              <Input
                value={editClassForm.subject}
                onChange={(e) => setEditClassForm({ ...editClassForm, subject: e.target.value })}
                placeholder="Nombre de la asignatura"
              />
            </div>
            <div>
              <Label>Grupo</Label>
              <Select 
                value={editClassForm.group_id} 
                onValueChange={(val) => setEditClassForm({ ...editClassForm, group_id: val })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona un grupo" />
                </SelectTrigger>
                <SelectContent>
                  {groups.map((group) => (
                    <SelectItem key={group.id} value={group.id}>
                      {group.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Descripción (opcional)</Label>
              <Input
                value={editClassForm.description}
                onChange={(e) => setEditClassForm({ ...editClassForm, description: e.target.value })}
                placeholder="Descripción de la clase"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button 
                variant="outline" 
                onClick={() => setEditClassDialogOpen(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleEditClass}
                className="flex-1 bg-blue-500 hover:bg-blue-600"
              >
                Guardar Cambios
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Control Escolar Confirmation Dialog */}
      <Dialog open={deleteCEDialogOpen} onOpenChange={setDeleteCEDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Confirmar eliminación
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-gray-700">
              ¿Está seguro de que desea eliminar al usuario de Control Escolar <strong>{ceToDelete?.name}</strong>?
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                Esta acción eliminará el acceso del usuario al sistema.
              </p>
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setDeleteCEDialogOpen(false)} className="flex-1">
                Cancelar
              </Button>
              <Button variant="destructive" onClick={handleDeleteCE} className="flex-1 bg-red-600 hover:bg-red-700">
                Eliminar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Orientador Confirmation Dialog */}
      <Dialog open={deleteOrientadorDialogOpen} onOpenChange={setDeleteOrientadorDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Confirmar eliminación
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-gray-700">
              ¿Está seguro de que desea eliminar al orientador <strong>{orientadorToDelete?.name}</strong>?
            </p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                <strong>⚠️ Nota:</strong> Las justificaciones creadas por este orientador permanecerán en el sistema.
              </p>
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setDeleteOrientadorDialogOpen(false)} className="flex-1">
                Cancelar
              </Button>
              <Button variant="destructive" onClick={handleDeleteOrientador} className="flex-1 bg-red-600 hover:bg-red-700">
                Eliminar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Prefecto Confirmation Dialog */}
      <Dialog open={deletePrefectoDialogOpen} onOpenChange={setDeletePrefectoDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Confirmar eliminación
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-gray-700">
              ¿Está seguro de que desea eliminar al prefecto <strong>{prefectoToDelete?.name}</strong>?
            </p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                <strong>⚠️ Nota:</strong> Los reportes creados por este prefecto permanecerán en el sistema y en el Historial de Conducta.
              </p>
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setDeletePrefectoDialogOpen(false)} className="flex-1">
                Cancelar
              </Button>
              <Button variant="destructive" onClick={handleDeletePrefecto} className="flex-1 bg-red-600 hover:bg-red-700" data-testid="confirm-delete-prefecto-btn">
                Eliminar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Subdirector Confirmation Dialog */}
      <Dialog open={deleteSubdirectorDialogOpen} onOpenChange={setDeleteSubdirectorDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Confirmar eliminación
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-gray-700">
              ¿Está seguro de que desea eliminar al subdirector <strong>{subdirectorToDelete?.name}</strong>?
            </p>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setDeleteSubdirectorDialogOpen(false)} className="flex-1">
                Cancelar
              </Button>
              <Button variant="destructive" onClick={handleDeleteSubdirector} className="flex-1 bg-red-600 hover:bg-red-700" data-testid="confirm-delete-subdirector-btn">
                Eliminar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Class Confirmation Dialog */}
      <Dialog open={deleteClassDialogOpen} onOpenChange={setDeleteClassDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Confirmar eliminación de clase
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-gray-700">
              ¿Estás seguro de que deseas eliminar la clase <strong>{classToDelete?.subject}</strong> del grupo <strong>{classToDelete?.group}</strong>?
            </p>
            <div className="bg-gray-50 rounded-lg p-3 text-sm">
              <p><strong>Profesor:</strong> {classToDelete?.teacher_name}</p>
              <p><strong>Alumnos:</strong> {classToDelete?.student_count}</p>
              <p><strong>Tutores vinculados:</strong> {classToDelete?.tutor_count}</p>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                <strong>Advertencia:</strong> Se eliminarán todos los registros de asistencia, tareas y vínculos con tutores de esta clase.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-sm text-amber-800">
              <p><strong>Recordatorio:</strong> ¿Ya avisó al profesor para que descargue su reporte en Excel antes del borrado?</p>
            </div>
            <div className="flex gap-3 pt-2">
              <Button 
                variant="outline" 
                onClick={() => setDeleteClassDialogOpen(false)}
                className="flex-1"
                disabled={deletingClass}
              >
                Cancelar
              </Button>
              <Button 
                variant="destructive"
                onClick={handleDeleteClass}
                className="flex-1 bg-red-600 hover:bg-red-700"
                disabled={deletingClass}
              >
                {deletingClass ? 'Eliminando...' : 'Eliminar Clase'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete All Classes Confirmation Dialog */}
      <Dialog open={deleteAllClassesDialogOpen} onOpenChange={setDeleteAllClassesDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Borrar TODAS las clases
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-gray-700">
              Estás a punto de eliminar <strong>todas las {institutionClasses.length} clases</strong> de la institución <strong>{selectedInstitution?.name}</strong>.
            </p>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800">
                <strong>Esta acción es irreversible.</strong> Se eliminarán todos los registros de asistencia, tareas y vínculos con tutores de todas las clases de esta institución.
              </p>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800 space-y-1">
              <p><strong>Se verá reflejado en:</strong></p>
              <p>- Profesores: desaparecerán todas sus clases</p>
              <p>- Tutores: no tendrán información de clases</p>
              <p>- Control Escolar: listas de clases disponibles quedarán vacías</p>
            </div>
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-sm text-amber-800">
              <p><strong>Recordatorio:</strong> ¿Ya avisó a los profesores para que descarguen sus reportes en Excel antes del borrado?</p>
            </div>
            <div className="flex gap-3 pt-2">
              <Button 
                variant="outline" 
                onClick={() => setDeleteAllClassesDialogOpen(false)}
                className="flex-1"
                disabled={deletingAllClasses}
              >
                Cancelar
              </Button>
              <Button 
                variant="destructive"
                onClick={handleDeleteAllClasses}
                className="flex-1 bg-red-600 hover:bg-red-700"
                disabled={deletingAllClasses}
                data-testid="confirm-delete-all-classes-btn"
              >
                {deletingAllClasses ? 'Eliminando...' : `Eliminar ${institutionClasses.length} clases`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Update Matrículas Dialog (merge import) */}
      <Dialog open={matriculaDialogOpen} onOpenChange={setMatriculaDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-blue-600" />
              Actualizar Matrículas
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateMatriculas} className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              Sube un Excel para el grupo <strong>{matriculaGroup?.name}</strong>. Los alumnos se emparejan
              por <strong>nombre</strong> y se les rellena la <strong>matrícula</strong>, sin borrar alumnos,
              clases, asistencias ni calificaciones. Consejo: usa el botón <strong>Descargar</strong>, llena la
              columna Matrícula y vuelve a subir el archivo.
            </div>
            <div>
              <Label>Archivo Excel (con columna Matrícula)</Label>
              <Input
                ref={matriculaFileRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => { setMatriculaFile(e.target.files?.[0] || null); setMatriculaResult(null); }}
                data-testid="matricula-file-input"
              />
              {matriculaFile && <p className="text-xs text-slate-500 mt-1">{matriculaFile.name}</p>}
            </div>

            {matriculaResult && (
              <div className="rounded-lg border border-slate-200 p-3 text-sm space-y-1" data-testid="matricula-result">
                <p className="text-green-700 font-semibold">✓ {matriculaResult.message}</p>
                <p className="text-slate-600">Alumnos actualizados: <strong>{matriculaResult.updated}</strong> · Aún sin matrícula: <strong>{matriculaResult.still_missing}</strong></p>
                {matriculaResult.ambiguous?.length > 0 && (
                  <p className="text-amber-700">Homónimos omitidos (edítalos manualmente): {matriculaResult.ambiguous.join(', ')}</p>
                )}
                {matriculaResult.not_found_in_group?.length > 0 && (
                  <p className="text-slate-500">No encontrados en el grupo: {matriculaResult.not_found_in_group.join(', ')}</p>
                )}
              </div>
            )}

            <div className="flex gap-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setMatriculaDialogOpen(false)}>
                Cerrar
              </Button>
              <Button
                type="submit"
                disabled={updatingMatriculas || !matriculaFile}
                className="flex-1 bg-blue-500 hover:bg-blue-600 disabled:opacity-50"
                data-testid="matricula-submit-btn"
              >
                {updatingMatriculas ? 'Actualizando...' : 'Actualizar matrículas'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>


      {/* Upload Group Dialog */}
      <Dialog open={uploadGroupDialogOpen} onOpenChange={setUploadGroupDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-green-600" />
              Importar Grupo desde Excel
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUploadGroup} className="space-y-4">
            <p className="text-sm text-slate-500">
              Se asignará automáticamente a <strong>{selectedInstitution?.name}</strong>
            </p>
            <div>
              <label className="text-sm font-medium">Archivo Excel (.xlsx) *</label>
              <Input
                type="file"
                accept=".xlsx,.xls"
                ref={uploadFileRef}
                onChange={(e) => setUploadGroupFile(e.target.files[0])}
                className="mt-1"
                data-testid="upload-group-file-input"
              />
              <p className="text-xs text-slate-400 mt-1">El nombre del grupo se tomará del nombre del archivo. El archivo debe tener una columna con los nombres de los alumnos.</p>
              <button
                type="button"
                onClick={downloadImportTemplate}
                data-testid="download-template-btn"
                className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline"
              >
                <Download className="w-3.5 h-3.5" /> Descargar plantilla (Matrícula, Nombre, Correo, Contraseña)
              </button>
              {uploadGroupFile && (
                <p className="text-xs text-green-700 mt-1" data-testid="upload-group-detected-name">
                  Nombre del grupo: <strong>{detectedUploadName}</strong>
                </p>
              )}
              {uploadNameDuplicate && (
                <p className="text-xs text-red-600 mt-1 font-semibold" data-testid="upload-group-dup-warning">
                  ⚠ Ya existe un grupo con ese nombre en esta institución. Renombra el archivo antes de importar.
                </p>
              )}
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setUploadGroupDialogOpen(false)} className="flex-1">
                Cancelar
              </Button>
              <Button type="submit" disabled={uploadingGroup || !uploadGroupFile || uploadNameDuplicate} className="flex-1 bg-green-500 hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="upload-group-submit-btn">
                {uploadingGroup ? 'Importando...' : 'Importar'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Group Students Dialog */}
      <Dialog open={viewGroupDialog} onOpenChange={setViewGroupDialog}>
        <DialogContent className="bg-white max-w-md max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>{viewingGroup?.name} — {viewingGroup?.student_names?.length || 0} alumnos</DialogTitle>
          </DialogHeader>
          <div className="max-h-[400px] overflow-y-auto">
            {(viewingGroup?.student_names || []).length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">No hay alumnos en este grupo</p>
            ) : (
              <div className="divide-y divide-slate-100">
                {(viewingGroup?.student_names || []).map((name, i) => (
                  <div key={i} className="py-2 px-1 flex items-center gap-2 text-sm">
                    <span className="text-slate-400 w-6 text-right">{i + 1}.</span>
                    <span className="text-slate-800">{name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Group Name Dialog */}
      <Dialog open={editGroupDialog} onOpenChange={setEditGroupDialog}>
        <DialogContent className="bg-white max-w-sm">
          <DialogHeader>
            <DialogTitle>Editar Nombre del Grupo</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditGroupSubmit} className="space-y-4">
            <Input
              value={editGroupName}
              onChange={(e) => setEditGroupName(e.target.value)}
              placeholder="Nombre del grupo"
            />
            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={() => setEditGroupDialog(false)} className="flex-1">
                Cancelar
              </Button>
              <Button type="submit" className="flex-1 bg-blue-500 hover:bg-blue-600">
                Guardar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Notify Professors Dialog */}
      <Dialog open={notifyProfsDialogOpen} onOpenChange={setNotifyProfsDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <Megaphone className="w-5 h-5" />
              Avisar a profesores
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Se enviará un aviso a todos los profesores de esta institución para que descarguen sus reportes antes del borrado de clases.
            </p>
            <div>
              <Label className="text-sm font-medium">Fecha programada para el borrado</Label>
              <Input
                type="date"
                value={notifyProfsFecha}
                onChange={(e) => setNotifyProfsFecha(e.target.value)}
                data-testid="notify-profs-fecha"
              />
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setNotifyProfsDialogOpen(false)} className="flex-1" disabled={sendingProfNotif}>
                Cancelar
              </Button>
              <Button
                onClick={sendNotifyProfessors}
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
                disabled={sendingProfNotif}
                data-testid="confirm-notify-profs"
              >
                {sendingProfNotif ? 'Enviando...' : 'Enviar Aviso'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Scale Groups Dialog */}
      <Dialog open={scaleGroupsDialogOpen} onOpenChange={setScaleGroupsDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-violet-600">
              <ArrowUpCircle className="w-5 h-5" />
              Escalar grupos al siguiente semestre
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Esta acción detectará automáticamente el tipo de semestre y actualizará los nombres de los grupos:
            </p>
            <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 text-sm space-y-1">
              <p><strong>Semestre NON (1,3,5):</strong> 1→2, 3→4, 5→6</p>
              <p><strong>Semestre PAR (2,4,6):</strong> 2→3, 4→5, 6→eliminado (egresados)</p>
            </div>
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-sm text-amber-800">
              <p><strong>Importante:</strong> Solo funciona si ya se borraron todas las clases de la institución.</p>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setScaleGroupsDialogOpen(false)} className="flex-1" disabled={scalingGroups}>
                Cancelar
              </Button>
              <Button
                onClick={handleScaleGroups}
                className="flex-1 bg-violet-500 hover:bg-violet-600 text-white"
                disabled={scalingGroups}
                data-testid="confirm-scale-groups"
              >
                {scalingGroups ? 'Escalando...' : 'Escalar Grupos'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default AdminDashboard;
